import type { FC, ReactNode } from 'react'
import type { Profile, ProjectCard as ProjectCardData } from '@/types'

import { useRef } from 'react'
import { useScroll } from 'motion/react'
import { Container } from '@/components/generals/container'
import { SectionLabel } from '@/components/generals/section-label'
import { ProjectCard } from '@/components/landing/project-card'
import { Reveal } from '@/components/motion/reveal'
import { useMediaQuery } from '@/hooks/use-media-query'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'

interface WorkSectionProps {
    profile: Profile | null
    projects: ProjectCardData[]
}

export const WorkSection: FC<WorkSectionProps> = ({ profile, projects }): ReactNode => {
    const stackRef = useRef<HTMLDivElement>(null)
    const desktop = useMediaQuery('(min-width: 768px)')
    const reduced = usePrefersReducedMotion()
    const stacking = desktop && !reduced
    const { scrollYProgress } = useScroll({ target: stackRef, offset: ['start start', 'end end'] })
    const count = String(projects.length).padStart(2, '0')

    return (
        <section id='work' className='relative bg-cream pt-20 pb-16 md:pt-32 md:pb-24'>
            <Container>
                <Reveal>
                    <SectionLabel index='02' label='Selected work' />
                </Reveal>

                <div className='mt-8 flex flex-wrap items-end justify-between gap-6'>
                    {profile?.work_title && (
                        <Reveal>
                            <h2 className='max-w-3xl font-heading text-[clamp(2.6rem,7vw,6rem)] font-extrabold leading-[0.95] tracking-[-0.035em]'>
                                <span data-mdlfy-cms={`profile:${profile.id}:work_title`}>{profile.work_title}</span>
                            </h2>
                        </Reveal>
                    )}

                    {projects.length > 0 && (
                        <p className='font-mono text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground'>
                            {count} case studies
                        </p>
                    )}
                </div>
            </Container>

            <div ref={stackRef} className='relative mt-10 md:mt-6'>
                {projects.map((project, index) => (
                    <ProjectCard
                        key={project.id}
                        project={project}
                        index={index}
                        total={projects.length}
                        progress={scrollYProgress}
                        stacking={stacking}
                    />
                ))}
            </div>

            {projects.length === 0 && (
                <Container>
                    <p className='rounded-[2rem] border-2 border-dashed border-foreground/15 p-10 text-center text-lg text-muted-foreground'>
                        New case studies are on their way.
                    </p>
                </Container>
            )}
        </section>
    )
}
